import React, { useState, useEffect } from 'react';
import api from '../api/api';

const LiveStreams = () => {
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLiveStreams = async () => {
    try {
      setLoading(true);
      const response = await api.get('/team-owners/live-streams');
      setLiveStreams(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching live streams:', err);
      setError('Failed to load live streams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveStreams();
    
    // Refresh every 2 minutes
    const interval = setInterval(fetchLiveStreams, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading && liveStreams.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return null; // Silently fail - don't show error on home page
  }

  if (liveStreams.length === 0) {
    // Show empty state instead of hiding completely
    return (
      <div className="bg-gradient-to-r from-gray-600 to-gray-700 rounded-lg shadow-lg dark:shadow-gray-900 p-4 mb-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center">
            <span className="relative flex h-3 w-3 mr-2">
              <span className="relative inline-flex rounded-full h-3 w-3 bg-gray-400"></span>
            </span>
            <h2 className="text-xl font-semibold text-white">Live Streams</h2>
          </div>
        </div>
        <div className="text-center mt-3">
          <p className="text-gray-300 text-sm">
            No live streams active. Check back later!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg shadow-lg dark:shadow-gray-900 p-4 mb-8">
      <div className="flex items-center mb-3">
        <div className="flex items-center">
          <span className="relative flex h-3 w-3 mr-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white dark:bg-gray-800 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white dark:bg-gray-800"></span>
          </span>
          <h2 className="text-xl font-bold text-white">LIVE NOW</h2>
        </div>
        <span className="ml-3 text-white text-sm">
          {liveStreams.length} {liveStreams.length === 1 ? 'stream' : 'streams'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {liveStreams.map((stream) => (
          <a
            key={stream.userId}
            href={`https://twitch.tv/${stream.username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-white transition-all duration-200 transform hover:scale-105"
          >
            {/* Thumbnail */}
            <div className="relative aspect-video">
              <img
                src={stream.thumbnailUrl}
                alt={stream.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center">
                <span className="relative flex h-2 w-2 mr-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white dark:bg-gray-800 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white dark:bg-gray-800"></span>
                </span>
                LIVE
              </div>
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs font-semibold">
                {stream.viewerCount.toLocaleString()} viewers
              </div>
            </div>

            {/* Stream Info */}
            <div className="p-3">
              <div className="flex items-start">
                {stream.profileImageUrl && (
                  <img
                    src={stream.profileImageUrl}
                    alt={stream.displayName}
                    className="w-8 h-8 rounded-full mr-2"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-sm truncate">
                    {stream.title}
                  </h3>
                  <p className="text-gray-400 text-xs mt-1">
                    {stream.owner?.team ? (
                      <>
                        <span className="font-semibold text-orange-400">
                          {stream.owner.team.city} {stream.owner.team.name}
                        </span>
                        {' • '}
                      </>
                    ) : null}
                    {stream.displayName}
                  </p>
                  {stream.gameName && (
                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                      {stream.gameName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-3 text-center text-white text-xs opacity-75">
        Auto-refreshes every 2 minutes
      </div>
    </div>
  );
};

export default LiveStreams;
